import { type FC, type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './auth-store';
import { usePermission } from './use-permission';
import { AxisFinanceLogo } from '@/acc-ui/components/AxisFinanceLogo';

/**
 * ProtectedRoute — wrapper for routes that require authentication and,
 * optionally, a specific permission.
 *
 * Flow:
 *   - If auth state is `unknown`, render a neutral splash while /me loads
 *   - If `anonymous`, redirect to /login (preserving the attempted URL)
 *   - If `authed` but missing required permission, render a 403 page
 *   - Otherwise, render children
 *
 * Every app route that should require a permission passes it here.
 */

export interface ProtectedRouteProps {
  children: ReactNode;
  permission?: string;
}

const LoadingSplash: FC = () => (
  <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-surface-page">
    <AxisFinanceLogo height={36} />
    <span className="mt-4 text-label text-text-tertiary">Signing you in…</span>
  </div>
);

const Forbidden: FC<{ missing: string }> = ({ missing }) => (
  <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-surface-page px-6">
    <AxisFinanceLogo height={36} />
    <h1 className="mt-6 text-title font-semibold text-text-primary">Access denied</h1>
    <p className="mt-1 text-label text-text-tertiary text-center max-w-md">
      You don't have permission to view this page. Ask your administrator to grant you
      <code className="mx-1.5 px-1.5 py-0.5 rounded bg-slate-900/5 text-caption">{missing}</code>
      via Access Manager.
    </p>
  </div>
);

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, permission }) => {
  const status = useAuthStore((s) => s.status);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const { has } = usePermission();
  const location = useLocation();

  useEffect(() => {
    if (status === 'unknown') void fetchMe();
  }, [status, fetchMe]);

  if (status === 'unknown') return <LoadingSplash />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (permission && !has(permission)) {
    return <Forbidden missing={permission} />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
